import React from 'react'
import './ObjectManager.css'

interface SceneObject {
  id: string
  name: string
  type: 'mesh' | 'points' | 'template' | 'grid' | 'annotation'
  visible: boolean
  selectable: boolean
}

interface ObjectManagerProps {
  objects: SceneObject[]
  onToggleVisibility: (objectId: string) => void
  onSelectObject: (objectId: string) => void
  onDeleteObject: (objectId: string) => void
  selectedObjectId?: string | null
}

export const ObjectManager: React.FC<ObjectManagerProps> = ({
  objects,
  onToggleVisibility,
  onSelectObject,
  onDeleteObject,
  selectedObjectId,
}) => {
  const getObjectIcon = (type: SceneObject['type']) => {
    switch (type) {
      case 'mesh': return '🦶'
      case 'points': return '📍'
      case 'template': return '📐'
      case 'grid': return '⊞'
      case 'annotation': return '📝'
      default: return '📄'
    }
  }

  const handleShowAll = () => {
    objects.forEach(obj => {
      if (!obj.visible) {
        onToggleVisibility(obj.id)
      }
    })
  }

  const handleHideAll = () => {
    objects.forEach(obj => {
      if (obj.visible) {
        onToggleVisibility(obj.id)
      }
    })
  }

  const handleRefresh = () => {
    console.log('Object Manager: Refresh requested')
    // Refresh is handled automatically by the scene object tracking
  }

  return (
    <div className="object-manager">
      <div className="object-manager-header">
        <span className="header-title">G-Object Manager</span>
      </div>

      <div className="object-list">
        {objects.length === 0 ? (
          <div className="empty-state">
            <p>No objects in scene</p>
            <p className="empty-hint">Import an STL file to get started</p>
          </div>
        ) : (
          objects.map(obj => (
          <div
            key={obj.id}
            className={`object-item ${selectedObjectId === obj.id ? 'selected' : ''}`}
            onClick={() => obj.selectable && onSelectObject(obj.id)}
            tabIndex={obj.selectable ? 0 : -1}
            onKeyDown={(e) => {
              if (obj.selectable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                onSelectObject(obj.id)
              }
            }}
            style={obj.selectable ? { cursor: 'pointer' } : undefined}
          >
            {/* Visibility Toggle Checkbox */}
            <label
              className="visibility-checkbox-container"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={obj.visible}
                onChange={() => onToggleVisibility(obj.id)}
                className="visibility-checkbox-input"
                title={obj.visible ? 'Hide object' : 'Show object'}
              />
              <span className={`visibility-checkbox-custom ${obj.visible ? 'checkbox-visible' : 'checkbox-hidden'}`}>
                {obj.visible ? '✓' : '✗'}
              </span>
            </label>

            <span className="object-icon">
              {getObjectIcon(obj.type)}
            </span>

            <span className="object-name">
              {obj.name}
            </span>

            {obj.selectable && (
              <button
                className="delete-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  onDeleteObject(obj.id)
                }}
                title="Delete object"
              >
                🗑️
              </button>
            )}
          </div>
          ))
        )}
      </div>

      <div className="object-manager-actions">
        <button
          className="action-btn"
          title="Show All"
          onClick={handleShowAll}
          disabled={objects.length === 0}
        >
          👁️
        </button>
        <button
          className="action-btn"
          title="Hide All"
          onClick={handleHideAll}
          disabled={objects.length === 0}
        >
          🚫
        </button>
        <button
          className="action-btn"
          title="Refresh"
          onClick={handleRefresh}
        >
          🔄
        </button>
      </div>
    </div>
  )
}
